import React, { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Form, Spinner, Table } from "react-bootstrap";
import Pagination from "../../Pagination";
import { NoDataFoundTable, PageLoader } from "../../../Components/NoDataFoundTable/NoDataFoundTable";
import { studyLabManagementFileServices } from "../../../APIServices/studyLabManagementFileServices";
import Swal from "sweetalert2";
import { useParams } from "react-router";
import { FaTrash } from "react-icons/fa6";
import { FaEdit } from "react-icons/fa";
import SplitPane from "react-split-pane";

function formatCell(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const createEmptyRow = (columns = []) =>
  columns.reduce((acc, column) => {
    acc[column] = "";
    return acc;
  }, {});

const normalizeSortValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return typeof value === "number" ? value : String(value).trim();
};

const sortRowsByConfig = (data, sortConfig = []) => {
  if (!sortConfig.length) return [...data];

  return [...data].sort((a, b) => {
    for (const { id, desc } of sortConfig) {
      const leftValue = normalizeSortValue(a?.[id]);
      const rightValue = normalizeSortValue(b?.[id]);

      let comparison = 0;

      if (leftValue === "" && rightValue === "") {
        comparison = 0;
      } else if (leftValue === "") {
        comparison = 1;
      } else if (rightValue === "") {
        comparison = -1;
      } else if (!Number.isNaN(Number(leftValue)) && !Number.isNaN(Number(rightValue))) {
        comparison = Number(leftValue) - Number(rightValue);
      } else {
        comparison = String(leftValue).localeCompare(String(rightValue), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      if (comparison !== 0) {
        return desc ? comparison * -1 : comparison;
      }
    }

    return 0;
  });
};

const ReadS3TableData = ({
  getStudyLabManagementData,
  fileData,
  s3Url,
  onMouseDown,
  tableRef,
}) => {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState([]);

  const extractColumns = (data) => {
    if (!data?.length) return [];
    const keys = new Set();

    data.forEach((row) => {
      Object.keys(row || {}).forEach((key) => keys.add(key));
    });

    return [...keys];
  };

  useEffect(() => {
    if (!s3Url) return;

    loadFileFromS3(s3Url);
  }, [s3Url]);

  const loadFileFromS3 = async (url) => {
    try {
      setLoading(true);
      setError("");
      setRows([]);
      setColumns([]);
      setSortConfig([]);

      const cacheBustedUrl = `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;
      const extension = url.split("?")[0].split(".").pop().toLowerCase();

      if (extension === "csv") {
        const response = await fetch(cacheBustedUrl, {
          cache: "no-store",
          headers: {
            pragma: "no-cache",
            "cache-control": "no-cache",
          },
        });
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: ({ data }) => {
            setRows(data);
            setColumns(extractColumns(data));
            setLoading(false);
          },
          error: () => {
            setError("Failed to parse CSV");
            setLoading(false);
          },
        });

        return;
      }

      if (extension === "json") {
        const response = await fetch(cacheBustedUrl, {
          cache: "no-store",
          headers: {
            pragma: "no-cache",
            "cache-control": "no-cache",
          },
        });
        const json = await response.json();

        const data = Array.isArray(json) ? json : json.data || [];

        setRows(data);
        setColumns(extractColumns(data));
        setLoading(false);
        return;
      }

      if (extension === "xls" || extension === "xlsx") {
        const response = await fetch(cacheBustedUrl, {
          cache: "no-store",
          headers: {
            pragma: "no-cache",
            "cache-control": "no-cache",
          },
        });
        const buffer = await response.arrayBuffer();

        const workbook = XLSX.read(buffer, { type: "array" });

        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const data = XLSX.utils.sheet_to_json(sheet, {
          defval: null,
        });

        setRows(data);
        setColumns(extractColumns(data));
        setLoading(false);
        return;
      }

      setError("Unsupported file type.");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError("Unable to load file.");
      setLoading(false);
    }
  };

  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const applySortConfig = (nextSortConfig) => {
    setSortConfig(nextSortConfig);
    setRows((prevRows) => sortRowsByConfig(prevRows, nextSortConfig));
  };

  const handleSortToggle = (column, isMultiSort = false) => {
    setError("");

    setSortConfig((prevSortConfig) => {
      const existing = prevSortConfig.find((entry) => entry.id === column);
      const nextSortConfig = isMultiSort
        ? [...prevSortConfig]
        : [];

      if (existing) {
        const updated = nextSortConfig.map((entry) =>
          entry.id === column ? { ...entry, desc: !entry.desc } : entry
        );
        const finalConfig = updated.length ? updated : [{ id: column, desc: !existing.desc }];
        setRows((currentRows) => sortRowsByConfig(currentRows, finalConfig));
        return finalConfig;
      }

      const newEntry = { id: column, desc: false };
      const finalConfig = isMultiSort ? [...nextSortConfig, newEntry] : [newEntry];
      setRows((currentRows) => sortRowsByConfig(currentRows, finalConfig));
      return finalConfig;
    });
  };

  const addColumn = () => {
    const proposedName = `column_${columns.length + 1}`;
    Swal.fire({
      title: "Add Column",
      padding: "1em",
      input: "text",
      inputValue: proposedName,
      inputPlaceholder: "Enter column name",
      showCancelButton: true,
      confirmButtonText: "Add",
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      inputValidator: (value) => {
        const trimmed = value?.trim();
        if (!trimmed) return "Column name cannot be empty.";
        if (columns.includes(trimmed)) return "Column already exists.";
        return null;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const trimmedName = result.value.trim();
        setError("");
        setColumns((prevColumns) => [...prevColumns, trimmedName]);
        setRows((prevRows) =>
          prevRows.map((row) => ({
            ...row,
            [trimmedName]: "",
          }))
        );
      }
    });
  };

  const renameColumn = (columnName) => {
    Swal.fire({
      title: "Rename Column",
      input: "text",
      padding: "1em",
      inputValue: columnName,
      inputPlaceholder: "Enter new column name",
      showCancelButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      inputValidator: (value) => {
        const trimmed = value?.trim();
        if (!trimmed) return "Column name cannot be empty.";
        if (trimmed === columnName) return "Name is unchanged.";
        if (columns.includes(trimmed)) return "A column with this name already exists.";
        return null;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const trimmedName = result.value.trim();
        setError("");
        setColumns((prevColumns) =>
          prevColumns.map((column) => (column === columnName ? trimmedName : column))
        );
        setRows((prevRows) =>
          prevRows.map((row) => {
            const updatedRow = { ...row };
            const value = updatedRow[columnName];
            delete updatedRow[columnName];
            updatedRow[trimmedName] = value ?? "";
            return updatedRow;
          })
        );
        setSortConfig((prevSortConfig) =>
          prevSortConfig.map((entry) =>
            entry.id === columnName ? { ...entry, id: trimmedName } : entry
          )
        );
      }
    });
  };

  const deleteColumn = (columnName) => {
    if (columns.length <= 1) {
      setError("At least one column must remain.");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `You want to delete the column "${columnName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setError("");
        setColumns((prevColumns) => prevColumns.filter((column) => column !== columnName));
        setRows((prevRows) =>
          prevRows.map((row) => {
            const updatedRow = { ...row };
            delete updatedRow[columnName];
            return updatedRow;
          })
        );
        setSortConfig((prevSortConfig) => prevSortConfig.filter((entry) => entry.id !== columnName));
      }
    });
  };

  const addRow = () => {
    setRows((prevRows) => [...prevRows, createEmptyRow(columns)]);
  };

  const deleteRow = (rowIndex) => {
    if (rows.length <= 1) {
      setError("At least one row must remain.");
      return;
    }
    Swal.fire({
      title: "Are you sure?",
      text: `You want to delete row ${rowIndex + 1}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setRows((prevRows) => prevRows.filter((_, index) => index !== rowIndex));
        setError("");
      }
    });
  };

  const updateCellValue = (rowIndex, column, value) => {
    setRows((prevRows) => {
      const nextRows = [...prevRows];
      const updatedRow = { ...(nextRows[rowIndex] || {}) };
      updatedRow[column] = value;
      nextRows[rowIndex] = updatedRow;
      return nextRows;
    });
  };

  const [buttonLoader, setButtonLoader] = useState(false);
  const { studyId } = useParams();
  const StudyMode = JSON.parse(localStorage.getItem("StudyMode"));

  const buildUpdatedDatasetFile = () => {
    const extension = (s3Url || "").split("?")[0].split(".").pop()?.toLowerCase() || "csv";
    const originalFileName = (s3Url || "").split("/").pop()?.split("?")[0] || `dataset.${extension}`;
    const activeColumns = columns.length ? columns : extractColumns(rows);

    const normalizedRows = rows.map((row) => {
      const normalizedRow = {};
      activeColumns.forEach((column) => {
        normalizedRow[column] = row?.[column] ?? "";
      });
      return normalizedRow;
    });

    if (extension === "xls" || extension === "xlsx") {
      const worksheet = XLSX.utils.json_to_sheet(normalizedRows, {
        defval: "",
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const arrayBuffer = XLSX.write(workbook, {
        bookType: extension === "xls" ? "xls" : "xlsx",
        type: "array",
      });

      const contentType =
        extension === "xls"
          ? "application/vnd.ms-excel"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      return new File([arrayBuffer], originalFileName, {
        type: contentType,
        lastModified: Date.now(),
      });
    }

    const content = Papa.unparse(normalizedRows, {
      columns: activeColumns,
    });

    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    return new File([blob], originalFileName || "dataset.csv", {
      type: "text/csv",
      lastModified: Date.now(),
    });
  };

  const prepareDatasetBuffer = () => {
    const formData = new FormData();
    Swal.fire({
      title: "Are you sure?",
      text: "You want to Update Study Lab File!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setButtonLoader(true);
        try {
          const file = buildUpdatedDatasetFile();
          formData.append("id", fileData?._id);
          formData.append("studyId", studyId);
          formData.append("fileName", fileData?.fileName || file.name);
          formData.append("mode", StudyMode);
          formData.append("file", file);
          const data = await studyLabManagementFileServices.updateStudyLabManagementData(formData);
          if (data?.statusCode === 200) {
            getStudyLabManagementData();
          }
        } catch (error) {
          console.error("Update dataset file failed:", error);
          setError("Unable to update the dataset file.");
        } finally {
          setButtonLoader(false);
        }
      }
    });
  };

  const displayedRows = useMemo(() => {
    if (!sortConfig.length) return rows;
    return sortRowsByConfig(rows, sortConfig);
  }, [rows, sortConfig]);

  const startIndex = (pageNumber - 1) * itemsPerPage;
  const paginatedRows = displayedRows.slice(startIndex, startIndex + itemsPerPage);
  const totalPage = Math.max(1, Math.ceil(displayedRows.length / itemsPerPage));

  useEffect(() => {
    if (pageNumber > totalPage) {
      setPageNumber(totalPage);
    }
  }, [pageNumber, totalPage]);

  return (
    <>
      <style>
        {`
          .custom-table thead th .header-icons {
            opacity: 0;
            transition: opacity 0.15s ease;
          }
          .custom-table thead th:hover .header-icons {
            opacity: 1;
          }
        `}
      </style>
      <div className="d-flex justify-content-end gap-2 p-2 border-bottom align-items-center flex-wrap">
        <button className="updateProfile" onClick={addColumn}>
          + Add Column
        </button>
        <button className="updateProfile" onClick={addRow}>
          + Add Row
        </button>
        <button
          disabled={buttonLoader}
          className="updateProfile"
          onClick={(e) => prepareDatasetBuffer(e)}
        >
          {buttonLoader ? (
            <div className="hstack gap-2">
              <Spinner size="sm" /> Saving...
            </div>
          ) : (
            "Save"
          )}
        </button>
      </div>
      <SplitPane split="vertical" minSize={"100%"} className="position-relative" style={{ height: "calc(100vh - 189px)" }}>
        {error ? (
          <div className="p-3 text-danger">{error}</div>
        ) : loading ? (
          <PageLoader />
        ) : displayedRows.length === 0 ? (
          <NoDataFoundTable MSG="No Data Found" />
        ) : (
          <>
            <Table ref={tableRef} className="m-0 custom-table w-auto" hover striped bordered>
              <thead className="position-sticky top-0 z-1">
                <tr>
                  <th style={{ minWidth: 55 }} className="text-center">Action</th>
                  {columns.map((col) => {
                    const currentSort = sortConfig.find((entry) => entry.id === col);

                    return (
                      <th
                        key={col}
                        onMouseDown={(e) => onMouseDown?.(e, 0)}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div
                            className="CP fw-bold px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSortToggle(col, e.shiftKey);
                            }}
                            title={
                              currentSort
                                ? `Sort by ${col} (${currentSort.desc ? "desc" : "asc"})`
                                : `Sort by ${col}`
                            }
                            style={{ textDecoration: "none" }}
                          >
                            {col}
                            {currentSort ? ` ${currentSort.desc ? "↓" : "↑"}` : ""}
                          </div>
                          <div className="d-flex gap-1 header-icons">
                            <FaEdit
                              className="text-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                renameColumn(col);
                              }}
                              title="Rename column"
                              style={{ cursor: "pointer" }}
                            />
                            <FaTrash
                              className="text-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteColumn(col);
                              }}
                              title="Delete column"
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, index) => {
                  const actualRowIndex = startIndex + index;

                  return (
                    <tr key={actualRowIndex}>
                      <td className="text-center">
                        <FaTrash
                          className="text-danger"
                          onClick={() => deleteRow(actualRowIndex)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                      {columns.map((col) => (
                        <td key={`${actualRowIndex}-${col}`} style={{ minWidth: 140, padding: 0 }}>
                          <Form.Control
                            type="text"
                            className="p-0 border-0 rounded-0 shadow-none bg-transparent"
                            value={formatCell(row[col])}
                            onChange={(e) => updateCellValue(actualRowIndex, col, e.target.value)}
                            style={{ minWidth: 120, width: "100%", padding: "8px 10px" }}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <div className="position-sticky bottom-0 start-0">
              <Pagination
                totalPage={totalPage}
                pageNumber={pageNumber}
                itemsPerPage={itemsPerPage}
                totalItems={displayedRows.length}
                setPageNumber={setPageNumber}
                setItemsPerPage={setItemsPerPage}
              />
            </div>
          </>
        )}
      </SplitPane>
    </>
  );
};

export default ReadS3TableData;